/// <reference types="gapi.auth2" />
/**
 * Local Ivy-compatible replacement for the abandoned `ng-gapi` package.
 * Public API (GoogleApiModule/GoogleApiService/GoogleAuthService/GoogleApiConfig/
 * NgGapiClientConfig/NG_GAPI_CONFIG) mirrors ng-gapi so existing usages keep working.
 */
import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Provider,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import ClientConfig = gapi.auth2.ClientConfig;
import GoogleAuth = gapi.auth2.GoogleAuth;

export interface NgGapiClientConfig extends ClientConfig {
  discoveryDocs: string[];
}

export class GoogleApiConfig {
  constructor(protected clientConfig: NgGapiClientConfig) {}

  public getClientConfig(): NgGapiClientConfig {
    return this.clientConfig;
  }
}

export const NG_GAPI_CONFIG = new InjectionToken<NgGapiClientConfig>('ng-gapi.config');

@Injectable()
export class GoogleApiService {
  private readonly gapiUrl: string = 'https://apis.google.com/js/api.js';
  private config: GoogleApiConfig;
  private gapiLoaded = false;
  private node: HTMLScriptElement | undefined;
  private observers: Array<{ next: (v: boolean) => void; complete: () => void }> = [];

  constructor(@Inject(NG_GAPI_CONFIG) config: NgGapiClientConfig) {
    this.config = new GoogleApiConfig(config);
    this.loadGapi().subscribe();
  }

  public onLoad(): Observable<boolean> {
    return this.loadGapi();
  }

  public getConfig(): GoogleApiConfig {
    return this.config;
  }

  private loadGapi(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.gapiLoaded) {
        observer.next(true);
        observer.complete();
      } else if (!this.node) {
        this.node = document.createElement('script');
        this.node.async = true;
        this.node.src = this.gapiUrl;
        this.node.type = 'text/javascript';
        this.node.onload = () => {
          this.gapiLoaded = true;
          while (this.observers.length) {
            const pending = this.observers.shift();
            if (pending) {
              pending.next(true);
              pending.complete();
            }
          }
          this.node = undefined;
        };
        this.node.onerror = () => {
          this.node = undefined;
        };
        document.getElementsByTagName('head')[0].appendChild(this.node);
      } else {
        this.observers.push(observer);
      }
    });
  }
}

@Injectable()
export class GoogleAuthService {
  private GoogleAuth: GoogleAuth | undefined = undefined;

  constructor(@Inject(GoogleApiService) private googleApi: GoogleApiService) {
    this.googleApi.onLoad().subscribe(() => {
      this.loadGapiAuth().subscribe();
    });
  }

  public getAuth(newInstance = false): Observable<GoogleAuth> {
    if (!this.GoogleAuth || newInstance) {
      return this.googleApi.onLoad().pipe(mergeMap(() => this.loadGapiAuth()));
    }
    return of(this.GoogleAuth);
  }

  private loadGapiAuth(): Observable<GoogleAuth> {
    return new Observable<GoogleAuth>((observer) => {
      gapi.load('auth2', () => {
        gapi.auth2
          .init(this.googleApi.getConfig().getClientConfig())
          .then((auth: GoogleAuth) => {
            this.GoogleAuth = auth;
            observer.next(auth);
            observer.complete();
          })
          .catch((err: unknown) => observer.error(err));
      });
    });
  }
}

@NgModule()
export class GoogleApiModule {
  static forRoot(gapiConfigProvider: Provider): ModuleWithProviders<GoogleApiModule> {
    return {
      ngModule: GoogleApiModule,
      providers: [gapiConfigProvider, GoogleAuthService, GoogleApiService],
    };
  }
}
